<template>
  <div class="scan-container">
    <el-dialog
      title="扫描二维码"
      :visible.sync="showScanner"
      width="90%"
      :show-close="true"
      class="scan-dialog"
    >
      <div v-if="cameraActive" class="scanner-area">
        <!-- 摄像头扫描区域 -->
        <qrcode-stream @decode="onDecode" @init="onInit" class="qrcode-stream">
          <div class="scanner-overlay">
            <div class="scanner-corner top-left"></div>
            <div class="scanner-corner top-right"></div>
            <div class="scanner-corner bottom-left"></div>
            <div class="scanner-corner bottom-right"></div>
            <div class="scanner-line"></div>
            <div class="scanner-hint">请对准防伪码二维码</div>
          </div>
        </qrcode-stream>
        <div class="scan-controls">
          <el-button type="danger" @click="stopCamera">取消扫描</el-button>
        </div>
      </div>

      <div v-else class="scanner-area">
        <!-- 扫描选项 -->
        <div class="scanner-overlay static-overlay">
          <div class="scanner-corner top-left"></div>
          <div class="scanner-corner top-right"></div>
          <div class="scanner-corner bottom-left"></div>
          <div class="scanner-corner bottom-right"></div>
          <div class="scanner-hint">请选择扫描方式</div>
        </div>
        
        <div class="scan-options">
          <el-button type="primary" icon="el-icon-camera" @click="startCamera">
            使用摄像头扫描
          </el-button>
          <div class="upload-divider">或</div>
          <el-upload
            class="upload-area"
            action="#"
            :http-request="uploadQrCode"
            :show-file-list="false"
            accept="image/*"
          >
            <el-button icon="el-icon-upload">
              上传二维码图片
            </el-button>
          </el-upload>
        </div>
      </div>
      
      <div class="manual-input">
        <p>或者手动输入防伪码</p>
        <el-input
          v-model="manualCode"
          placeholder="请输入防伪码"
          class="manual-input-field"
        ></el-input>
        <el-button 
          type="primary" 
          @click="verifyManualCode" 
          :disabled="!manualCode"
        >
          验证
        </el-button>
      </div>
    </el-dialog>

    <!-- 结果显示对话框 -->
    <el-dialog
      :title="resultTitle"
      :visible.sync="showResult"
      width="80%"
      :show-close="true"
      class="result-dialog"
    >
      <div class="result-container" :class="resultStatus">
        <img :src="resultIcon" alt="验证结果" class="result-icon" />
        <div class="result-message">{{ resultMessage }}</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showResult = false">关闭</el-button>
        <el-button type="primary" @click="closeResult">确定</el-button>
      </span>
    </el-dialog>

    <!-- 触发扫描的按钮 -->
    <el-button 
      type="primary" 
      icon="el-icon-camera" 
      @click="openScanner"
      class="scan-button"
    >
      扫描防伪码
    </el-button>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import jsQR from 'jsqr';
import { verifyCode } from '@/config/api';

export default {
  name: 'ScanQR',
  components: {
    QrcodeStream
  },
  data() {
    return {
      showScanner: false,
      showResult: false,
      cameraActive: false,
      cameraError: '',
      manualCode: '',
      resultStatus: '',
      resultMessage: '',
      resultTitle: '验证结果',
      resultIcon: require('@/assets/iconSuccess.png')
    };
  },
  methods: {
    openScanner() {
      this.showScanner = true;
    },
    
    closeScanner() {
      this.stopCamera();
      this.showScanner = false;
      this.manualCode = '';
    },
    
    closeResult() {
      this.showResult = false;
      this.closeScanner();
    },
    
    startCamera() {
      this.cameraActive = true;
    },
    
    stopCamera() {
      this.cameraActive = false;
    },
    
    // 摄像头初始化处理
    async onInit(promise) {
      try {
        await promise;
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          this.cameraError = '需要摄像头权限来进行扫描';
        } else if (error.name === 'NotFoundError') {
          this.cameraError = '找不到摄像头设备';
        } else if (error.name === 'NotSupportedError') {
          this.cameraError = '您的设备不支持摄像头捕获';
        } else if (error.name === 'NotReadableError') {
          this.cameraError = '摄像头无法访问，可能正被其他应用使用';
        } else if (error.name === 'OverconstrainedError') {
          this.cameraError = '摄像头不满足所需的约束条件';
        } else {
          this.cameraError = `未知错误: ${error.message}`;
        }
        this.$notify.error({
          title: '摄像头错误',
          message: this.cameraError
        });
        this.stopCamera();
      }
    },
    
    // 处理摄像头扫描结果
    onDecode(code) {
      this.stopCamera();
      this.verifyCode(code);
    },
    
    // 处理上传的二维码图片
    uploadQrCode(options) {
      const file = options.file;
      
      // 检查是否是图片
      if (!file.type.startsWith('image/')) {
        this.$notify.error({
          title: '文件错误',
          message: '请上传图片文件'
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // 创建Canvas以便处理图像
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          // 获取图像数据
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          // 使用jsQR库解析二维码
          const code = jsQR(imageData.data, imageData.width, imageData.height);
          
          if (code) {
            this.verifyCode(code.data);
          } else {
            this.$notify.error({
              title: '解析失败',
              message: '无法从图片中识别二维码'
            });
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    verifyManualCode() {
      if (this.manualCode) {
        this.verifyCode(this.manualCode);
      }
    },
    
    async verifyCode(code) {
      try {
        const result = await verifyCode(code);
        this.showVerificationResult(result);
      } catch (error) {
        this.showVerificationResult({
          status: 'error',
          message: '验证服务异常，请稍后再试'
        });
      }
    },
    
    showVerificationResult(result) {
      // 设置结果图标
      if (result.status === 'valid') {
        this.resultIcon = require('@/assets/iconSuccess.png');
        this.resultTitle = '验证成功';
      } else if (result.status === 'warning') {
        this.resultIcon = require('@/assets/iconError.png');
        this.resultTitle = '警告';
      } else {
        this.resultIcon = require('@/assets/iconError.png');
        this.resultTitle = '验证失败';
      }
      
      this.resultStatus = result.status;
      this.resultMessage = result.message;
      this.showScanner = false;
      this.showResult = true;
    }
  }
};
</script>

<style scoped>
.scan-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.scan-dialog {
  border-radius: 12px;
}

.scanner-area {
  position: relative;
  width: 100%;
  height: 300px;
  background-color: #f0f0f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 20px;
}

.qrcode-stream {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scan-controls {
  position: absolute;
  bottom: 10px;
  z-index: 10;
}

.static-overlay {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.1);
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.scanner-corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border-color: #4f29f0;
  border-style: solid;
  border-width: 0;
}

.top-left {
  top: 40px;
  left: 40px;
  border-top-width: 3px;
  border-left-width: 3px;
}

.top-right {
  top: 40px;
  right: 40px;
  border-top-width: 3px;
  border-right-width: 3px;
}

.bottom-left {
  bottom: 40px;
  left: 40px;
  border-bottom-width: 3px;
  border-left-width: 3px;
}

.bottom-right {
  bottom: 40px;
  right: 40px;
  border-bottom-width: 3px;
  border-right-width: 3px;
}

.scanner-line {
  position: absolute;
  top: 40px;
  left: 40px;
  right: 40px;
  height: 2px;
  background-color: #4f29f0;
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% {
    top: 40px;
  }
  50% {
    top: calc(100% - 40px);
  }
  100% {
    top: 40px;
  }
}

.scanner-hint {
  position: absolute;
  bottom: 10px;
  color: #333;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px 10px;
  border-radius: 4px;
}

.scan-options {
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.upload-divider {
  margin: 10px 0;
  color: #909399;
}

.manual-input {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.manual-input p {
  margin-bottom: 10px;
  color: #909399;
}

.manual-input-field {
  width: 80%;
  margin-bottom: 10px;
}

.result-dialog {
  text-align: center;
}

.result-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.result-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 20px;
}

.result-message {
  font-size: 16px;
  line-height: 1.5;
  color: #333;
}

.valid {
  color: #67c23a;
}

.warning {
  color: #e6a23c;
}

.invalid, .error {
  color: #f56c6c;
}

.scan-button {
  margin-top: 20px;
  font-size: 16px;
  padding: 12px 20px;
}
</style>